import { Fraction } from '@learnforge/demo-math/numeric';
import { type Expr, type FuncName, num, variable, add, mul, pow, func } from './expr';
import { Polynomial } from './polynomial';

export class ParseError extends Error {
  constructor(
    message: string,
    public readonly position: number,
    public readonly i18nKey?: string,
  ) {
    super(message);
    this.name = 'ParseError';
  }
}

export type Comparator = '<' | '<=' | '>' | '>=' | '=';

export interface ParseOptions {
  variableSet?: ReadonlySet<string>;
  allowConstants?: boolean;
}

// Token types
type TokenKind =
  | 'number'
  | 'identifier'
  | 'op'
  | 'caret'
  | 'lparen'
  | 'rparen'
  | 'bar'
  | 'comma'
  | 'eq'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'sqrt'
  | 'eof';

interface Token {
  kind: TokenKind;
  value: string;
  position: number;
}

const FUNCTION_NAMES = new Set<FuncName>([
  'sqrt',
  'cbrt',
  'log',
  'ln',
  'log2',
  'log10',
  'abs',
  'sin',
  'cos',
  'tan',
]);

class Tokenizer {
  private pos: number = 0;

  constructor(private src: string) {}

  peek(): string {
    if (this.pos >= this.src.length) return '\0';
    return this.src[this.pos]!;
  }

  peekNext(): string {
    if (this.pos + 1 >= this.src.length) return '\0';
    return this.src[this.pos + 1]!;
  }

  advance(): string {
    const ch = this.peek();
    this.pos += 1;
    return ch;
  }

  skipWhitespace(): void {
    while (/\s/.test(this.peek())) {
      this.advance();
    }
  }

  nextToken(): Token {
    this.skipWhitespace();

    const start = this.pos;
    const ch = this.peek();

    // EOF
    if (ch === '\0') {
      return { kind: 'eof', value: '', position: start };
    }

    // Unicode sqrt
    if (ch === '√') {
      this.advance();
      return { kind: 'sqrt', value: '√', position: start };
    }

    // Operators and symbols
    if (ch === '(') {
      this.advance();
      return { kind: 'lparen', value: '(', position: start };
    }
    if (ch === ')') {
      this.advance();
      return { kind: 'rparen', value: ')', position: start };
    }
    if (ch === '|') {
      this.advance();
      return { kind: 'bar', value: '|', position: start };
    }
    if (ch === ',') {
      this.advance();
      return { kind: 'comma', value: ',', position: start };
    }
    if (ch === '^') {
      this.advance();
      return { kind: 'caret', value: '^', position: start };
    }

    // Comparison operators (must come before +/-)
    if (ch === '<') {
      this.advance();
      if (this.peek() === '=') {
        this.advance();
        return { kind: 'lte', value: '<=', position: start };
      }
      return { kind: 'lt', value: '<', position: start };
    }
    if (ch === '>') {
      this.advance();
      if (this.peek() === '=') {
        this.advance();
        return { kind: 'gte', value: '>=', position: start };
      }
      return { kind: 'gt', value: '>', position: start };
    }
    if (ch === '=') {
      this.advance();
      return { kind: 'eq', value: '=', position: start };
    }

    // Arithmetic operators
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      this.advance();
      return { kind: 'op', value: ch, position: start };
    }

    // Numbers (integers and decimals). NOTE: `/` is always the division
    // operator — we deliberately do NOT tokenize `a/b` as a single fraction
    // literal, because that would break left-associative division (e.g.
    // `x/3/4` must be `(x/3)/4 = x/12`, not `x/(3/4) = 4x/3`).
    if (/\d/.test(ch)) {
      let numStr = '';
      while (/\d/.test(this.peek())) {
        numStr += this.advance();
      }

      // Check for decimal
      if (this.peek() === '.' && /\d/.test(this.peekNext())) {
        numStr += this.advance(); // consume '.'
        while (/\d/.test(this.peek())) {
          numStr += this.advance();
        }
      }

      return { kind: 'number', value: numStr, position: start };
    }

    // Identifiers (letters, can be single or multi-char)
    if (/[a-zA-Z_]/.test(ch)) {
      let ident = '';
      while (/[a-zA-Z0-9_]/.test(this.peek())) {
        ident += this.advance();
      }
      return { kind: 'identifier', value: ident, position: start };
    }

    // Unknown character
    throw new ParseError(
      `Unexpected character '${ch}'`,
      start,
      'message.parse-error.unexpected-token',
    );
  }
}

class Parser {
  private tokens: Token[];
  private pos: number = 0;

  constructor(
    src: string,
    private options: ParseOptions = {},
  ) {
    const tokenizer = new Tokenizer(src);
    const tokens: Token[] = [];
    let token = tokenizer.nextToken();
    while (token.kind !== 'eof') {
      tokens.push(token);
      token = tokenizer.nextToken();
    }
    tokens.push(token); // Add EOF
    this.tokens = tokens;

    // Normalize variableSet
    if (!this.options.variableSet) {
      this.options.variableSet = new Set(['x']);
    }
  }

  private peek(): Token {
    if (this.pos >= this.tokens.length) {
      return this.tokens[this.tokens.length - 1]!;
    }
    return this.tokens[this.pos]!;
  }

  private advance(): Token {
    const token = this.peek();
    if (token.kind !== 'eof') {
      this.pos += 1;
    }
    return token;
  }

  private expect(kind: TokenKind): Token {
    const token = this.peek();
    if (token.kind !== kind) {
      throw new ParseError(
        `Expected ${kind}, got ${token.kind}`,
        token.position,
        'message.parse-error.unexpected-token',
      );
    }
    return this.advance();
  }

  private negate(expr: Expr): Expr {
    if (expr.kind === 'num') {
      return num(expr.value.neg());
    }
    return mul(num(-1), expr);
  }

  parse(): Expr {
    const result = this.expr();

    // Ensure we've consumed all tokens
    if (this.peek().kind !== 'eof') {
      throw new ParseError(
        `Unexpected token '${this.peek().value}' at end of expression`,
        this.peek().position,
        'message.parse-error.unexpected-token',
      );
    }

    return result;
  }

  // expr := term (("+" | "-") term)*
  private expr(): Expr {
    const terms: Expr[] = [];
    terms.push(this.term());

    while (this.peek().kind === 'op' && (this.peek().value === '+' || this.peek().value === '-')) {
      const op = this.advance().value;
      const right = this.term();

      if (op === '+') {
        terms.push(right);
      } else {
        // "-" → add negated
        terms.push(this.negate(right));
      }
    }

    if (terms.length === 1) {
      return terms[0]!;
    }

    return add(...terms);
  }

  // term := factor (("*" | "/") factor | implicit_mul factor)*
  private term(): Expr {
    const factors: Expr[] = [];
    factors.push(this.factor());

    while (true) {
      const current = this.peek();

      // Explicit * or /
      if (current.kind === 'op' && (current.value === '*' || current.value === '/')) {
        const op = this.advance().value;
        const right = this.factor();

        if (op === '*') {
          factors.push(right);
        } else {
          // "/" → mul(right, pow(right, -1))
          factors.push(pow(right, num(-1)));
        }
      }
      // Implicit multiplication
      else if (this.canStartAtom(current)) {
        const right = this.factor();
        factors.push(right);
      } else {
        break;
      }
    }

    if (factors.length === 1) {
      return factors[0]!;
    }

    return mul(...factors);
  }

  // factor := unary "^" factor | unary
  // ^ is right-associative
  private factor(): Expr {
    const base = this.unary();

    if (this.peek().kind === 'caret') {
      this.advance();
      const exp = this.factor(); // Right-associative
      return pow(base, exp);
    }

    return base;
  }

  // unary := "-" unary | call (note: unary + is not supported)
  private unary(): Expr {
    const current = this.peek();

    if (current.kind === 'op' && current.value === '-') {
      const op = this.advance().value;

      // Check if next token can start an operand, if not it's an error
      const next = this.peek();
      if (!this.canStartUnary(next)) {
        throw new ParseError(
          `Expected expression after unary '${op}'`,
          next.position,
          'message.parse-error.unexpected-token',
        );
      }

      const operand = this.unary();
      return this.negate(operand);
    }

    // √ prefix operator
    if (current.kind === 'sqrt') {
      this.advance();
      const operand = this.unary();
      return func('sqrt', operand);
    }

    return this.call();
  }

  private canStartUnary(token: Token): boolean {
    return (
      token.kind === 'number' ||
      token.kind === 'identifier' ||
      token.kind === 'lparen' ||
      token.kind === 'bar' ||
      token.kind === 'sqrt' ||
      (token.kind === 'op' && token.value === '-')
    );
  }

  // call := identifier "(" expr ("," expr)* ")" | atom
  private call(): Expr {
    // Check for function call
    if (this.peek().kind === 'identifier' && this.peekAhead(1).kind === 'lparen') {
      const ident = this.peek().value;

      if (FUNCTION_NAMES.has(ident as FuncName)) {
        this.advance(); // consume identifier
        this.advance(); // consume lparen

        const args: Expr[] = [];

        if (this.peek().kind !== 'rparen') {
          args.push(this.expr());

          while (this.peek().kind === 'comma') {
            this.advance();
            args.push(this.expr());
          }
        }

        this.expect('rparen');
        return func(ident as FuncName, ...args);
      }
    }

    return this.atom();
  }

  // atom := number | identifier | "(" expr ")" | "|" expr "|"
  //      | "sqrt(" expr ")"
  private atom(): Expr {
    const current = this.peek();

    // Number
    if (current.kind === 'number') {
      const token = this.advance();
      return this.parseNumber(token.value);
    }

    // Identifier (variable)
    if (current.kind === 'identifier') {
      const token = this.advance();
      const name = token.value;

      // Check if it's a known function followed by (
      if (FUNCTION_NAMES.has(name as FuncName) && this.peek().kind === 'lparen') {
        // Rewind and let call() handle it
        this.pos -= 1;
        return this.call();
      }

      // Check if it's a variable
      if (this.options.variableSet!.has(name)) {
        return variable(name);
      }

      // Check if it's allowed as a constant
      if (this.options.allowConstants) {
        return variable(name);
      }

      // Unknown variable
      throw new ParseError(
        `Unknown variable '${name}'`,
        token.position,
        'message.parse-error.unknown-variable',
      );
    }

    // Parenthesized expression
    if (current.kind === 'lparen') {
      this.advance();
      const inner = this.expr();
      this.expect('rparen');
      return inner;
    }

    // Absolute value |expr|
    if (current.kind === 'bar') {
      const barStart = current.position;
      this.advance();
      const inner = this.expr();
      if (this.peek().kind !== 'bar') {
        throw new ParseError(
          `Expected closing '|' for absolute value`,
          barStart,
          'message.parse-error.unexpected-token',
        );
      }
      this.advance(); // consume closing bar
      return func('abs', inner);
    }

    throw new ParseError(
      `Unexpected token ${current.kind}`,
      current.position,
      'message.parse-error.unexpected-token',
    );
  }

  private peekAhead(n: number): Token {
    const index = this.pos + n;
    if (index >= this.tokens.length) {
      return this.tokens[this.tokens.length - 1]!;
    }
    return this.tokens[index]!;
  }

  private canStartAtom(token: Token): boolean {
    // For implicit multiplication, we only check for tokens that should trigger it
    // Note: 'bar' is excluded because closing bars should not trigger implicit mult
    return (
      token.kind === 'number' ||
      token.kind === 'identifier' ||
      token.kind === 'lparen' ||
      token.kind === 'sqrt'
    );
  }

  private parseNumber(str: string): Expr {
    // Handle decimals and integers (number tokens never contain '/')
    if (str.includes('.')) {
      const value = parseFloat(str);
      return num(Fraction.from(value));
    }

    const value = parseInt(str, 10);
    return num(value);
  }
}

export function parse(src: string, options?: ParseOptions): Expr {
  const parser = new Parser(src, options);
  return parser.parse();
}

export function parseEquation(src: string, options?: ParseOptions): { lhs: Expr; rhs: Expr } {
  const parser = new Parser(src, options);

  // Find the top-level '=' token
  let eqCount = 0;
  let eqPos = -1;
  let parenDepth = 0;
  const tokens = (parser as unknown as { tokens: Array<{ kind: TokenKind; position: number }> })
    .tokens;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) continue;
    if (token.kind === 'lparen') {
      parenDepth += 1;
    } else if (token.kind === 'rparen') {
      parenDepth -= 1;
    } else if (token.kind === 'eq' && parenDepth === 0) {
      eqCount += 1;
      eqPos = i;
    }
  }

  if (eqCount !== 1) {
    throw new ParseError(
      `Expected exactly one '=' in equation, found ${eqCount}`,
      src.length,
      'message.parse-error.unexpected-token',
    );
  }

  // Parse LHS
  const eqToken = tokens[eqPos];
  if (!eqToken) {
    throw new ParseError(
      'Equation parse error',
      src.length,
      'message.parse-error.unexpected-token',
    );
  }
  const lhsParser = new Parser(src.substring(0, eqToken.position), options);
  const lhs = lhsParser.parse();

  // Parse RHS
  const rhsParser = new Parser(src.substring(eqToken.position + 1), options);
  const rhs = rhsParser.parse();

  return { lhs, rhs };
}

export function parseInequality(
  src: string,
  options?: ParseOptions,
): { lhs: Expr; comparator: Exclude<Comparator, '='>; rhs: Expr } {
  const parser = new Parser(src, options);

  // Find the top-level inequality comparator
  let compPos = -1;
  let compKind: TokenKind | null = null;
  let parenDepth = 0;
  const tokens = (
    parser as unknown as { tokens: Array<{ kind: TokenKind; position: number; value: string }> }
  ).tokens;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (!token) continue;
    if (token.kind === 'lparen') {
      parenDepth += 1;
    } else if (token.kind === 'rparen') {
      parenDepth -= 1;
    } else if (
      (token.kind === 'lt' ||
        token.kind === 'lte' ||
        token.kind === 'gt' ||
        token.kind === 'gte') &&
      parenDepth === 0
    ) {
      if (compPos !== -1) {
        throw new ParseError(
          `Expected exactly one comparison operator, found multiple`,
          src.length,
          'message.parse-error.unexpected-token',
        );
      }
      compPos = i;
      compKind = token.kind;
    } else if (token.kind === 'eq' && parenDepth === 0) {
      throw new ParseError(
        `Expected inequality, not equation (=)`,
        token.position,
        'message.parse-error.unexpected-token',
      );
    }
  }

  if (compPos === -1) {
    throw new ParseError(
      `Expected inequality operator (<, <=, >, >=)`,
      src.length,
      'message.parse-error.unexpected-token',
    );
  }

  // Parse LHS
  const compToken = tokens[compPos];
  if (!compToken) {
    throw new ParseError(
      'Inequality parse error',
      src.length,
      'message.parse-error.unexpected-token',
    );
  }
  const lhsParser = new Parser(src.substring(0, compToken.position), options);
  const lhs = lhsParser.parse();

  // Parse RHS
  const rhsParser = new Parser(src.substring(compToken.position + compToken.value.length), options);
  const rhs = rhsParser.parse();

  const comparatorMap: { [key: string]: Exclude<Comparator, '='> } = {
    lt: '<',
    lte: '<=',
    gt: '>',
    gte: '>=',
  };

  const comparator = comparatorMap[compKind!] as Exclude<Comparator, '='>;

  return { lhs, comparator, rhs };
}

/**
 * Helper: compute GCD of two integers.
 */
function gcdSimple(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

/**
 * Helper: simplify a non-negative integer square root by extracting perfect square factors.
 * Returns { coeff, radicand } such that coeff² * radicand = n.
 * The radicand is square-free (minimal).
 */
function simplifyIntSqrt(n: number): { coeff: number; radicand: number } {
  if (n <= 0) return { coeff: 0, radicand: n };

  let coeff = 1;
  let remaining = n;

  // Try all perfect squares up to sqrt(remaining)
  for (let i = 2; i * i <= remaining; i++) {
    const sq = i * i;
    while (remaining % sq === 0) {
      coeff *= i;
      remaining /= sq;
    }
  }

  return { coeff, radicand: remaining };
}

export function format(
  expr: Expr,
  options?: { useUnicodeSuperscripts?: boolean; parenStyle?: 'minimal' | 'full'; locale?: string },
): string {
  const useUnicode = options?.useUnicodeSuperscripts ?? false;

  const formatRec = (e: Expr, parentPrecedence: number): string => {
    switch (e.kind) {
      case 'num': {
        return e.value.toString();
      }

      case 'var': {
        return e.name;
      }

      case 'add': {
        // Order terms by descending degree (polynomial-like)
        const orderedChildren = [...e.children].sort((a, b) => {
          const degA = getDegree(a);
          const degB = getDegree(b);
          return degB - degA;
        });

        const parts: string[] = [];
        for (const child of orderedChildren) {
          const isNegative = isNegativeTerm(child);
          const absStr = formatTermAbsolute(child);

          if (parts.length === 0) {
            // First term
            if (isNegative) {
              parts.push(`-${absStr}`);
            } else {
              parts.push(absStr);
            }
          } else {
            // Subsequent terms
            if (isNegative) {
              parts.push(` - ${absStr}`);
            } else {
              parts.push(` + ${absStr}`);
            }
          }
        }

        const result = parts.join('');
        if (parentPrecedence > 1) {
          return `(${result})`;
        }
        return result;
      }

      case 'mul': {
        const result = formatMulTerm(e);
        if (parentPrecedence > 2) {
          return `(${result})`;
        }
        return result;
      }

      case 'pow': {
        if (e.exp.kind === 'num' && e.exp.value.den === 1) {
          if (e.exp.value.num === 1) return formatRec(e.base, parentPrecedence);
          if (e.exp.value.num === 0) return '1';
        }

        const base = formatRec(e.base, 3);
        const exp = formatRec(e.exp, 3);

        if (useUnicode && e.exp.kind === 'num' && e.exp.value.den === 1) {
          const expNum = e.exp.value.num;
          const superscripts: { [key: string]: string } = {
            '0': '⁰',
            '1': '¹',
            '2': '²',
            '3': '³',
            '4': '⁴',
            '5': '⁵',
            '6': '⁶',
            '7': '⁷',
            '8': '⁸',
            '9': '⁹',
            '-': '⁻',
          };
          const expStr = String(expNum);
          const unicodeExp = expStr
            .split('')
            .map((ch) => superscripts[ch] ?? ch)
            .join('');
          return `${base}${unicodeExp}`;
        }

        const result = `${base}^${exp}`;
        if (parentPrecedence > 3) {
          return `(${result})`;
        }
        return result;
      }

      case 'func': {
        if (e.name === 'abs') {
          // Render abs(x) as |x|
          const arg = formatRec(e.args[0]!, 0);
          return `|${arg}|`;
        }

        // Special handling for sqrt in display mode
        if (e.name === 'sqrt' && useUnicode && e.args.length === 1) {
          const arg = e.args[0]!;
          if (arg.kind === 'num' && arg.value.num >= 0 && arg.value.den > 0) {
            const f = arg.value;
            const p = f.num;
            const q = f.den;

            // Rationalise: √(p/q) = √(p·q) / q
            // Simplify √(p·q) to extract perfect square factors: coeff² · radicand
            const product = p * q;
            const { coeff, radicand } = simplifyIntSqrt(product);

            // Result: (coeff / q) · √radicand
            // Reduce coeff / q
            const g = gcdSimple(coeff, q);
            const coeffReduced = coeff / g;
            const qReduced = q / g;

            if (radicand === 1) {
              // Perfect square: just the reduced coefficient
              if (qReduced === 1) {
                return coeffReduced.toString();
              }
              // (coeffReduced / qReduced) as a fraction
              return `${coeffReduced}/${qReduced}`;
            }

            // Non-perfect-square radicand
            if (coeffReduced === 1 && qReduced === 1) {
              // Just √radicand
              return `√${radicand}`;
            } else if (qReduced === 1) {
              // coeff · √radicand
              return `${coeffReduced}√${radicand}`;
            } else {
              // (coeffReduced / qReduced) · √radicand
              if (coeffReduced === 1) {
                return `(1/${qReduced})√${radicand}`;
              }
              return `(${coeffReduced}/${qReduced})√${radicand}`;
            }
          }
        }

        const args = e.args.map((arg) => formatRec(arg, 0)).join(', ');
        return `${e.name}(${args})`;
      }

      case 'sigma': {
        return `sigma(${e.index}, ${formatRec(e.from, 0)}, ${formatRec(e.to, 0)}, ${formatRec(e.body, 0)})`;
      }
    }
  };

  // Helper: get polynomial degree (main variable x)
  const getDegree = (e: Expr): number => {
    switch (e.kind) {
      case 'num':
        return -Infinity;
      case 'var':
        return 1;
      case 'pow':
        if (e.exp.kind === 'num' && e.exp.value.den === 1) {
          return e.exp.value.num;
        }
        return 0;
      case 'mul': {
        let maxDeg = 0;
        for (const child of e.children) {
          const deg = getDegree(child);
          if (!Number.isNaN(deg) && isFinite(deg) && deg > -Infinity) {
            maxDeg = Math.max(maxDeg, deg);
          }
        }
        return maxDeg;
      }
      default:
        return 0;
    }
  };

  // Helper: fold every numeric factor of a `mul` into a single coefficient
  // (autoSimplify does not collapse num × num, so a product can carry more
  // than one 'num' child; taking only the first would silently drop the rest).
  const numericCoefficient = (children: Expr[]): Fraction => {
    let coeff = Fraction.ONE;
    for (const c of children) {
      if (c.kind === 'num') {
        coeff = coeff.mul(c.value);
      }
    }
    return coeff;
  };

  // Helper: check if a term is negative
  const isNegativeTerm = (e: Expr): boolean => {
    if (e.kind === 'num') {
      return e.value.num < 0;
    }
    if (e.kind === 'mul') {
      const hasNumChild = e.children.some((c) => c.kind === 'num');
      if (hasNumChild) {
        return numericCoefficient(e.children).num < 0;
      }
      return false;
    }
    return false;
  };

  // Helper: format term with absolute value (without leading sign)
  const formatTermAbsolute = (e: Expr): string => {
    if (e.kind === 'num') {
      const absValue = new Fraction(Math.abs(e.value.num), e.value.den);
      return absValue.toString();
    }
    if (e.kind === 'mul') {
      const children = e.children;
      const hasNumChild = children.some((c) => c.kind === 'num');
      const nonNumChildren = children.filter((c) => c.kind !== 'num');
      const coeff = numericCoefficient(children);

      let numStr = '';
      if (hasNumChild) {
        const absValue = new Fraction(Math.abs(coeff.num), coeff.den);
        numStr = absValue.toString();
      }

      // Fraction coefficient with variable factors: render as <vars>/<den>
      if (hasNumChild && coeff.den !== 1 && nonNumChildren.length > 0) {
        const n = Math.abs(coeff.num);
        const varsStr = nonNumChildren.map((c) => formatMulFactor(c)).join('');
        return `${n === 1 ? '' : String(n)}${varsStr}/${coeff.den}`;
      }

      // Handle special cases for coefficients
      if (numStr === '1' && nonNumChildren.length > 0) {
        // Coefficient of 1: omit it
        return nonNumChildren.map((c) => formatMulFactor(c)).join('');
      } else if (numStr === '-1' && nonNumChildren.length > 0) {
        // Coefficient of -1 (absolute value): render as 1
        return nonNumChildren.map((c) => formatMulFactor(c)).join('');
      } else {
        // Render coefficient followed by variables
        const parts: string[] = [];
        if (numStr) {
          parts.push(numStr);
        }
        parts.push(...nonNumChildren.map((c) => formatMulFactor(c)));
        return parts.join('');
      }
    }
    // For other types, use regular formatting
    return formatRec(e, 2);
  };

  // Helper: format mul term with implicit multiplication
  const formatMulTerm = (e: Expr): string => {
    if (e.kind !== 'mul') {
      return formatRec(e, 2);
    }

    const children = e.children;
    const hasNumChild = children.some((c) => c.kind === 'num');
    const nonNumChildren = children.filter((c) => c.kind !== 'num');
    const coeff = numericCoefficient(children);

    let numStr = '';
    if (hasNumChild) {
      numStr = coeff.toString();
    }

    // Fraction coefficient with variable factors: render as <vars>/<den>
    if (hasNumChild && coeff.den !== 1 && nonNumChildren.length > 0) {
      const n = Math.abs(coeff.num);
      const varsStr = nonNumChildren.map((c) => formatMulFactor(c)).join('');
      const sign = coeff.num < 0 ? '-' : '';
      return `${sign}${n === 1 ? '' : String(n)}${varsStr}/${coeff.den}`;
    }

    // Handle special cases for coefficients
    if (numStr === '1' && nonNumChildren.length > 0) {
      // Coefficient of 1: omit it
      return nonNumChildren.map((c) => formatMulFactor(c)).join('');
    } else if (numStr === '-1' && nonNumChildren.length > 0) {
      // Coefficient of -1: render as just the negation
      return '-' + nonNumChildren.map((c) => formatMulFactor(c)).join('');
    } else {
      // Render coefficient (possibly with ±) followed by variables
      const parts: string[] = [];
      if (numStr) {
        parts.push(numStr);
      }
      parts.push(...nonNumChildren.map((c) => formatMulFactor(c)));
      return parts.join('');
    }
  };

  // Helper: format a single factor in multiplication (add parens if needed)
  const formatMulFactor = (e: Expr): string => {
    switch (e.kind) {
      case 'add': {
        const inner = formatRec(e, 1);
        return `(${inner})`;
      }
      case 'pow': {
        // Check if exponent is negative (division)
        if (e.exp.kind === 'num' && e.exp.value.num < 0) {
          // This is x^(-n), render as 1/x^n or similar
          const baseStr = formatRec(e.base, 3);
          const absExp = Math.abs(e.exp.value.num);
          if (useUnicode) {
            const superscripts: { [key: string]: string } = {
              '0': '⁰',
              '1': '¹',
              '2': '²',
              '3': '³',
              '4': '⁴',
              '5': '⁵',
              '6': '⁶',
              '7': '⁷',
              '8': '⁸',
              '9': '⁹',
            };
            const expStr = String(absExp);
            const unicodeExp = expStr
              .split('')
              .map((ch) => superscripts[ch] ?? ch)
              .join('');
            return `1/${baseStr}${unicodeExp}`;
          }
          return `1/${baseStr}^${absExp}`;
        }
        return formatRec(e, 2);
      }
      default: {
        return formatRec(e, 2);
      }
    }
  };

  return formatRec(expr, 0);
}

export function parsePolynomial(src: string, varName: string): Polynomial {
  const expr = parse(src, { variableSet: new Set([varName]) });
  const poly = Polynomial.fromExpr(expr, varName);
  if (poly === null) {
    throw new ParseError(
      `Not a polynomial in ${varName}: ${src}`,
      0,
      'message.parse-error.not-a-polynomial',
    );
  }
  return poly;
}
